import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { 
  ChevronRight, 
  ChevronLeft,
  CheckCircle2,
  Loader2,
  Rocket,
  Clock,
  Sparkles,
  Check,
} from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { Checkbox } from './ui/checkbox';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to publish height to parent iframe
const publishHeight = () => {
  const height = document.documentElement.scrollHeight;
  window.parent.postMessage({ type: 'resize', height }, '*');
};

// Helper to notify parent to scroll to top
const scrollParentToTop = () => {
  window.parent.postMessage({ type: 'scrollToTop' }, '*');
};

// V2 Plan options - Producer, Agency, Enterprise
const planOptions = [
  { 
    id: 'producer', 
    name: 'Producer', 
    price: '$59',
    period: '/month',
    bestFor: 'Agents and producers who need a quoting and underwriting toolkit to support their own sales conversations',
    description: 'Run quotes with LiveQuote, use QuoteIQ and the full underwriting toolkit, send consumer quote links, and manage leads in Pulse.',
    additionalInfo: 'First user $59/mo, additional users $29/mo',
    features: [
      'LiveQuote - agent quoter',
      'QuoteIQ - underwriting intelligence',
      'Full underwriting toolkit',
      'Pulse - CRM and lead management',
      'Consumer quote links',
    ],
  },
  { 
    id: 'agency', 
    name: 'Agency', 
    price: '$199',
    period: '/month',
    bestFor: 'Agencies who need agent-side quoting plus consumer-facing lead generation, branded quote pages, and embedded website experiences',
    description: 'Everything in Producer, plus branded quote pages, embedded website quoting, lead funnels, application workflows, and white-label customization.',
    additionalInfo: '2 users included, additional users $29/mo',
    features: [
      'Everything in Producer',
      'Beacon - lead capture funnels',
      'Public quote pages',
      'Embedded website quoting',
      'White-label customization',
    ],
    popular: true,
  },
  { 
    id: 'enterprise', 
    name: 'Enterprise', 
    price: 'Custom',
    period: '',
    bestFor: 'IMOs, BGAs, call centers, CRM partners, and larger organizations',
    description: 'Custom infrastructure, onboarding, integrations, and scaling support. Fully white-labeled administration area and portal.',
    additionalInfo: 'Contact us for pricing',
    features: [
      'White-labeled admin portal',
      'Multi-agency support',
      'API and integrations access',
      'Custom implementation',
      'Dedicated support',
    ],
  },
];

// Agency size options
const agencySizeOptions = [
  { value: '1', label: 'Just me' },
  { value: '2-5', label: '2-5 people' },
  { value: '6-10', label: '6-10 people' },
  { value: '11-25', label: '11-25 people' },
  { value: '26-50', label: '26-50 people' },
  { value: '50+', label: '50+ people' },
];

// States licensed options
const statesLicensedOptions = [
  { value: '1-5', label: '1-5 states' },
  { value: '6-10', label: '6-10 states' },
  { value: '11-25', label: '11-25 states' },
  { value: '26-40', label: '26-40 states' },
  { value: '41-50', label: '41-50 states' },
  { value: 'all', label: 'All 50 states' },
];

// Ad platforms
const adPlatforms = [
  { id: 'facebook', label: 'Facebook' },
  { id: 'google', label: 'Google' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'twitter', label: 'Twitter/X' },
  { id: 'other', label: 'Other' },
];

// Phone number formatter
const formatPhoneNumber = (value) => {
  const phoneNumber = value.replace(/\D/g, '');
  if (phoneNumber.length < 4) return phoneNumber;
  if (phoneNumber.length < 7) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

// Validation schemas
const contactSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string()
    .min(10, 'Phone number must be at least 10 digits')
    .regex(/^[\d\s\-\(\)\+]+$/, 'Please enter a valid phone number'),
  company_name: z.string().min(2, 'Company name must be at least 2 characters'),
});

// Animation variants
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

// Loading skeleton component
const FormSkeleton = () => (
  <div className="min-h-fit bg-transparent py-8 px-4 animate-pulse">
    <div className="max-w-full w-full" style={{ maxWidth: '100%' }}>
      <div className="text-center mb-8">
        <div className="h-6 bg-slate-200 rounded-full w-48 mx-auto mb-4"></div>
        <div className="h-8 bg-slate-200 rounded-lg w-56 mx-auto mb-3"></div>
        <div className="h-4 bg-slate-200 rounded w-80 mx-auto"></div>
      </div>
      <div className="mb-8">
        <div className="flex justify-between mb-2">
          <div className="h-4 bg-slate-200 rounded w-20"></div>
          <div className="h-4 bg-slate-200 rounded w-24"></div>
        </div>
        <div className="h-2 bg-slate-200 rounded-full"></div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="h-6 bg-slate-200 rounded w-24 mb-2"></div>
            <div className="h-10 bg-slate-200 rounded w-32 mb-4"></div>
            <div className="h-20 bg-slate-200 rounded w-full mb-4"></div>
            <div className="space-y-2">
              {[1, 2, 3, 4].map((j) => (
                <div key={j} className="h-4 bg-slate-200 rounded w-full"></div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const WaitlistFormWidgetV2 = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  
  // Form data state
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [hasWebsite, setHasWebsite] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [usesCRM, setUsesCRM] = useState(null);
  const [crmName, setCrmName] = useState('');
  const [agencySize, setAgencySize] = useState('');
  const [statesLicensed, setStatesLicensed] = useState('');
  const [runsPaidAds, setRunsPaidAds] = useState(null);
  const [adPlatformsSelected, setAdPlatformsSelected] = useState([]);
  const [otherPlatform, setOtherPlatform] = useState('');
  const [contactData, setContactData] = useState({});

  const totalSteps = 4;
  const progress = (currentStep / totalSteps) * 100;

  const contactForm = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      company_name: '',
    },
  });

  // Set up iframe height communication with parent
  useEffect(() => {
    document.documentElement.style.margin = '0';
    document.documentElement.style.padding = '0';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.background = 'transparent';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.overflow = 'hidden';
    document.body.style.background = 'transparent';

    const initTimeout = setTimeout(() => {
      setIsLoading(false);
      requestAnimationFrame(() => {
        publishHeight();
      });
    }, 150);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(publishHeight);
      resizeObserver.observe(document.body);
    }

    window.addEventListener('resize', publishHeight);

    return () => {
      clearTimeout(initTimeout);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', publishHeight);
    };
  }, []);

  useEffect(() => {
    const timeout = setTimeout(publishHeight, 100);
    return () => clearTimeout(timeout);
  }, [currentStep, submissionSuccess, isLoading]);

  const goToNextStep = useCallback(() => {
    setCurrentStep(prev => prev + 1);
    scrollParentToTop();
  }, []);

  const goToPrevStep = useCallback(() => {
    setCurrentStep(prev => prev - 1);
    scrollParentToTop();
  }, []);

  const handlePlatformToggle = (platformId) => {
    setAdPlatformsSelected(prev => 
      prev.includes(platformId) 
        ? prev.filter(id => id !== platformId)
        : [...prev, platformId]
    );
  };

  const handleContactSubmit = async (data) => {
    setContactData(data);
    setIsSubmitting(true);
    
    let platforms = runsPaidAds ? [...adPlatformsSelected] : [];
    if (adPlatformsSelected.includes('other') && otherPlatform) {
      platforms = platforms.filter(p => p !== 'other');
      platforms.push(`Other: ${otherPlatform}`);
    }
    
    // Map V2 plan IDs to plan names for the submission
    const planNameMap = {
      'producer': 'Producer ($59/mo)',
      'agency': 'Agency ($199/mo)',
      'enterprise': 'Enterprise Custom',
    };
    
    const submissionData = {
      ...data,
      plan_interest: planNameMap[selectedPlan] || selectedPlan,
      has_website: hasWebsite,
      website_url: hasWebsite ? websiteUrl : null,
      uses_crm: usesCRM,
      crm_name: usesCRM ? crmName : null,
      agency_size: agencySize,
      states_licensed: statesLicensed,
      runs_paid_ads: runsPaidAds,
      ad_platforms: platforms,
      form_version: 'v2', // Track which form version was used
    };

    try {
      await axios.post(`${API}/waitlist`, submissionData);
      setSubmissionSuccess(true);
      scrollParentToTop();
    } catch (error) {
      console.error('Submission error:', error);
      alert('There was an error submitting your application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceedStep1 = selectedPlan !== null;
  const canProceedStep2 = hasWebsite !== null && usesCRM !== null && agencySize && statesLicensed;
  const canProceedStep3 = runsPaidAds !== null && (runsPaidAds === false || adPlatformsSelected.length > 0);

  if (isLoading) {
    return <FormSkeleton />;
  }

  return (
    <div className="min-h-fit bg-transparent py-8 px-4">
      <div className="max-w-full w-full" style={{ maxWidth: '100%' }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Clock className="w-4 h-4" />
            Exciting Updates Coming Soon
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Reserve Your Spot
          </h1>
          <p className="text-slate-600 text-base max-w-2xl mx-auto">
            Be the first to access the all-in-one life insurance quoting, CRM, and lead generation platform built for agents and agencies. Current subscribers aren't affected, but new sign-ups will join our waitlist for the upgraded platform.
          </p>
        </motion.div>

        {/* Benefits - Only on Step 1 */}
        {currentStep === 1 && !submissionSuccess && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8"
          >
            <div className="flex items-center gap-3 bg-[#e6faf7] p-4 rounded-xl">
              <Rocket className="w-8 h-8 text-[#10c0a5]" />
              <div>
                <p className="font-medium text-slate-900">First Access</p>
                <p className="text-sm text-slate-600">Launch in order of signup</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-[#e6faf7] p-4 rounded-xl">
              <Sparkles className="w-8 h-8 text-[#10c0a5]" />
              <div>
                <p className="font-medium text-slate-900">Better Features</p>
                <p className="text-sm text-slate-600">Enhanced functionality</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-[#e6faf7] p-4 rounded-xl">
              <CheckCircle2 className="w-8 h-8 text-[#10c0a5]" />
              <div>
                <p className="font-medium text-slate-900">New Platform</p>
                <p className="text-sm text-slate-600">Easier installation</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Progress Bar */}
        {!submissionSuccess && (
          <div className="mb-8">
            <div className="flex justify-between text-sm text-slate-500 mb-2">
              <span>Step {currentStep} of {totalSteps}</span>
              <span>{Math.round(progress)}% Complete</span>
            </div>
            <Progress value={progress} className="h-2 bg-slate-100" />
          </div>
        )}

        {/* Main Content */}
        <AnimatePresence mode="wait">
          {/* Step 1: Plan Selection - V2 Pricing */}
          {currentStep === 1 && !submissionSuccess && (
            <motion.div
              key="step1"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.4 }}
              data-testid="waitlist-v2-step-1"
            >
              <div className="text-center mb-8">
                <span className="text-sm font-medium text-[#10c0a5] uppercase tracking-wider">Step 1</span>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mt-2 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Which plan interests you?
                </h2>
                <p className="text-slate-600">Select the plan that best fits your needs</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8 max-w-4xl mx-auto">
                {planOptions.map((plan) => (
                  <button
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`relative text-left transition-all flex flex-col overflow-hidden rounded-xl ${
                      selectedPlan === plan.id
                        ? 'ring-2 ring-[#10c0a5] shadow-xl'
                        : 'shadow-md hover:shadow-lg'
                    }`}
                    data-testid={`plan-${plan.id}`}
                  >
                    {/* Top accent bar */}
                    <div className={`h-1.5 w-full ${
                      plan.popular 
                        ? 'bg-gradient-to-r from-[#10c0a5] via-[#0ea892] to-[#10c0a5]' 
                        : 'bg-[#10c0a5]'
                    }`} />
                    
                    <div className="p-4 bg-white flex flex-col flex-grow">
                      {plan.popular && (
                        <div className="mb-2">
                          <span className="inline-block bg-[#10c0a5] text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide">
                            Most Popular
                          </span>
                        </div>
                      )}
                      
                      {/* Header */}
                      <div className="mb-3">
                        <h3 className="text-lg font-bold text-slate-800">{plan.name}</h3>
                        <div className="flex items-baseline gap-0.5 mt-1">
                          <span className="text-3xl font-bold text-slate-900">{plan.price}</span>
                          <span className="text-slate-400 text-sm">{plan.period}</span>
                        </div>
                      </div>

                      {/* Best For - teal background like original */}
                      <div className="bg-[#e6faf7] border-l-2 border-[#10c0a5] rounded-r-lg px-3 py-2 mb-3">
                        <p className="text-[10px] font-bold text-[#10c0a5] uppercase tracking-wider mb-0.5">Best For</p>
                        <p className="text-xs text-slate-700 leading-snug">{plan.bestFor}</p>
                      </div>

                      {/* Description */}
                      <p className="text-xs text-slate-500 mb-3 leading-relaxed">{plan.description}</p>

                      {/* Features section header */}
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-2">What's Included</p>
                      
                      {/* Features in compact grid */}
                      <div className="grid grid-cols-1 gap-1.5 mb-3 flex-grow">
                        {plan.features.map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-xs text-slate-600">
                            <div className="w-4 h-4 rounded-full bg-[#e6faf7] flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Check className="w-2.5 h-2.5 text-[#10c0a5]" />
                            </div>
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>

                      {/* Additional Info / Pricing note */}
                      <div className="pt-2 border-t border-slate-100 mt-auto">
                        <p className="text-[11px] font-semibold text-[#10c0a5]">{plan.additionalInfo}</p>
                      </div>

                      {/* Selection indicator */}
                      {selectedPlan === plan.id && (
                        <div className="absolute top-4 right-3">
                          <div className="w-5 h-5 bg-[#10c0a5] rounded-full flex items-center justify-center shadow-sm">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>

              <div className="flex justify-end">
                <button
                  onClick={goToNextStep}
                  disabled={!canProceedStep1}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                    canProceedStep1
                      ? 'bg-[#10c0a5] hover:bg-[#0ea892] text-white'
                      : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  }`}
                >
                  Continue
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Business Info */}
          {currentStep === 2 && !submissionSuccess && (
            <motion.div
              key="step2"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.4 }}
              className="bg-white rounded-2xl shadow-xl border border-slate-100 p-4 sm:p-8"
              data-testid="waitlist-v2-step-2"
            >
              <div className="text-center mb-8">
                <span className="text-sm font-medium text-[#10c0a5] uppercase tracking-wider">Step 2</span>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mt-2 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Tell us about your business
                </h2>
                <p className="text-slate-600">Help us understand your current setup</p>
              </div>

              <div className="space-y-6">
                <div>
                  <Label className="text-base font-medium text-slate-900 mb-3 block">
                    Do you currently have a website?
                  </Label>
                  <div className="flex gap-4">
                    <button
                      onClick={() => setHasWebsite(true)}
                      className={`flex-1 p-3 rounded-xl border-2 font-medium transition-all ${
                        hasWebsite === true
                          ? 'border-[#10c0a5] bg-[#e6faf7] text-[#10c0a5]'
                          : 'border-slate-200 text-slate-700 hover:border-slate-300'
                      }`}
                    >
                      Yes
                    </button>
                    <button
                      onClick={() => { setHasWebsite(false); setWebsiteUrl(''); }}
                      className={`flex-1 p-3 rounded-xl border-2 font-medium transition-all ${
                        hasWebsite === false
                          ? 'border-[#10c0a5] bg-[#e6faf7] text-[#10c0a5]'
                          : 'border-slate-200 text-slate-700 hover:border-slate-300'
                      }`}
                    >
                      No
                    </button>
                  </div>
                  {hasWebsite && (
                    <div className="mt-3">
                      <Input
                        type="url"
                        placeholder="https://yourwebsite.com"
                        value={websiteUrl}
                        onChange={(e) => setWebsiteUrl(e.target.value)}
                        className="w-full"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-base font-medium text-slate-900 mb-3 block">
                    Do you currently use a CRM?
                  </Label>
                  <div className="flex gap-4">
                    <button
                      onClick={() => setUsesCRM(true)}
                      className={`flex-1 p-3 rounded-xl border-2 font-medium transition-all ${
                        usesCRM === true
                          ? 'border-[#10c0a5] bg-[#e6faf7] text-[#10c0a5]'
                          : 'border-slate-200 text-slate-700 hover:border-slate-300'
                      }`}
                    >
                      Yes
                    </button>
                    <button
                      onClick={() => { setUsesCRM(false); setCrmName(''); }}
                      className={`flex-1 p-3 rounded-xl border-2 font-medium transition-all ${
                        usesCRM === false
                          ? 'border-[#10c0a5] bg-[#e6faf7] text-[#10c0a5]'
                          : 'border-slate-200 text-slate-700 hover:border-slate-300'
                      }`}
                    >
                      No
                    </button>
                  </div>
                  {usesCRM && (
                    <div className="mt-3">
                      <Input
                        type="text"
                        placeholder="Which CRM do you use?"
                        value={crmName}
                        onChange={(e) => setCrmName(e.target.value)}
                        className="w-full"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-base font-medium text-slate-900 mb-3 block">
                    How many people are in your agency?
                  </Label>
                  <Select value={agencySize} onValueChange={setAgencySize}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select agency size" />
                    </SelectTrigger>
                    <SelectContent>
                      {agencySizeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-base font-medium text-slate-900 mb-3 block">
                    How many states are you licensed in?
                  </Label>
                  <Select value={statesLicensed} onValueChange={setStatesLicensed}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select number of states" />
                    </SelectTrigger>
                    <SelectContent>
                      {statesLicensedOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-between mt-8">
                <button
                  onClick={goToPrevStep}
                  className="flex items-center gap-2 px-6 py-3 text-slate-600 hover:text-slate-900 font-medium transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                  Back
                </button>
                <button
                  onClick={goToNextStep}
                  disabled={!canProceedStep2}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                    canProceedStep2
                      ? 'bg-[#10c0a5] hover:bg-[#0ea892] text-white'
                      : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  }`}
                >
                  Continue
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Advertising */}
          {currentStep === 3 && !submissionSuccess && (
            <motion.div
              key="step3"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.4 }}
              className="bg-white rounded-2xl shadow-xl border border-slate-100 p-4 sm:p-8"
              data-testid="waitlist-v2-step-3"
            >
              <div className="text-center mb-8">
                <span className="text-sm font-medium text-[#10c0a5] uppercase tracking-wider">Step 3</span>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mt-2 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Tell us about your advertising
                </h2>
                <p className="text-slate-600">Help us understand your marketing efforts</p>
              </div>

              <div className="space-y-6">
                <div>
                  <Label className="text-base font-medium text-slate-900 mb-3 block">
                    Do you currently run paid ads?
                  </Label>
                  <div className="flex gap-4">
                    <button
                      onClick={() => setRunsPaidAds(true)}
                      className={`flex-1 p-3 rounded-xl border-2 font-medium transition-all ${
                        runsPaidAds === true
                          ? 'border-[#10c0a5] bg-[#e6faf7] text-[#10c0a5]'
                          : 'border-slate-200 text-slate-700 hover:border-slate-300'
                      }`}
                    >
                      Yes
                    </button>
                    <button
                      onClick={() => { setRunsPaidAds(false); setAdPlatformsSelected([]); setOtherPlatform(''); }}
                      className={`flex-1 p-3 rounded-xl border-2 font-medium transition-all ${
                        runsPaidAds === false
                          ? 'border-[#10c0a5] bg-[#e6faf7] text-[#10c0a5]'
                          : 'border-slate-200 text-slate-700 hover:border-slate-300'
                      }`}
                    >
                      No
                    </button>
                  </div>
                </div>

                {runsPaidAds && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                  >
                    <Label className="text-base font-medium text-slate-900 mb-3 block">
                      Which platforms do you advertise on? (Select all that apply)
                    </Label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {adPlatforms.map((platform) => (
                        <div key={platform.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={platform.id}
                            checked={adPlatformsSelected.includes(platform.id)}
                            onCheckedChange={() => handlePlatformToggle(platform.id)}
                          />
                          <label
                            htmlFor={platform.id}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                          >
                            {platform.label}
                          </label>
                        </div>
                      ))}
                    </div>
                    {adPlatformsSelected.includes('other') && (
                      <div className="mt-3">
                        <Input
                          type="text"
                          placeholder="Please specify other platform(s)"
                          value={otherPlatform}
                          onChange={(e) => setOtherPlatform(e.target.value)}
                          className="w-full"
                        />
                      </div>
                    )}
                  </motion.div>
                )}
              </div>

              <div className="flex justify-between mt-8">
                <button
                  onClick={goToPrevStep}
                  className="flex items-center gap-2 px-6 py-3 text-slate-600 hover:text-slate-900 font-medium transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                  Back
                </button>
                <button
                  onClick={goToNextStep}
                  disabled={!canProceedStep3}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                    canProceedStep3
                      ? 'bg-[#10c0a5] hover:bg-[#0ea892] text-white'
                      : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  }`}
                >
                  Continue
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 4: Contact Info */}
          {currentStep === 4 && !submissionSuccess && (
            <motion.div
              key="step4"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.4 }}
              className="bg-white rounded-2xl shadow-xl border border-slate-100 p-4 sm:p-8"
              data-testid="waitlist-v2-step-4"
            >
              <div className="text-center mb-8">
                <span className="text-sm font-medium text-[#10c0a5] uppercase tracking-wider">Step 4</span>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mt-2 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Your contact information
                </h2>
                <p className="text-slate-600">We'll use this to reach out when your spot is ready</p>
              </div>

              <form onSubmit={contactForm.handleSubmit(handleContactSubmit)} className="space-y-5">
                <div>
                  <Label htmlFor="name" className="text-sm font-medium text-slate-700 mb-2 block">
                    Your Name *
                  </Label>
                  <Input
                    id="name"
                    {...contactForm.register('name')}
                    placeholder="John Smith"
                    className={contactForm.formState.errors.name ? 'border-red-300' : ''}
                  />
                  {contactForm.formState.errors.name && (
                    <p className="mt-1 text-sm text-red-500">{contactForm.formState.errors.name.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="company_name" className="text-sm font-medium text-slate-700 mb-2 block">
                    Company/Agency Name *
                  </Label>
                  <Input
                    id="company_name"
                    {...contactForm.register('company_name')}
                    placeholder="Your Insurance Agency"
                    className={contactForm.formState.errors.company_name ? 'border-red-300' : ''}
                  />
                  {contactForm.formState.errors.company_name && (
                    <p className="mt-1 text-sm text-red-500">{contactForm.formState.errors.company_name.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="phone" className="text-sm font-medium text-slate-700 mb-2 block">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    {...contactForm.register('phone')}
                    placeholder="(555) 555-5555"
                    onChange={(e) => {
                      const formatted = formatPhoneNumber(e.target.value);
                      contactForm.setValue('phone', formatted);
                    }}
                    className={contactForm.formState.errors.phone ? 'border-red-300' : ''}
                  />
                  {contactForm.formState.errors.phone && (
                    <p className="mt-1 text-sm text-red-500">{contactForm.formState.errors.phone.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-slate-700 mb-2 block">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    {...contactForm.register('email')}
                    placeholder="you@example.com"
                    className={contactForm.formState.errors.email ? 'border-red-300' : ''}
                  />
                  {contactForm.formState.errors.email && (
                    <p className="mt-1 text-sm text-red-500">{contactForm.formState.errors.email.message}</p>
                  )}
                </div>

                <div className="flex justify-between pt-4">
                  <button
                    type="button"
                    onClick={goToPrevStep}
                    className="flex items-center gap-2 px-6 py-3 text-slate-600 hover:text-slate-900 font-medium transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex items-center gap-2 px-8 py-3 bg-[#10c0a5] hover:bg-[#0ea892] text-white rounded-xl font-medium transition-all disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Join Waitlist
                        <ChevronRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          )}

          {/* Success State */}
          {submissionSuccess && (
            <motion.div
              key="success"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              className="bg-white rounded-2xl shadow-xl border border-slate-100 p-4 sm:p-8 text-center"
              data-testid="waitlist-v2-success"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
                className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#e6faf7] mb-6"
              >
                <CheckCircle2 className="w-10 h-10 text-[#10c0a5]" />
              </motion.div>

              <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                You're on the Waitlist!
              </h2>
              <p className="text-slate-600 text-lg mb-6 max-w-md mx-auto">
                Thank you for your interest in Quoteplicity! We'll contact you as soon as 
                a spot opens up on our new platform.
              </p>

              <div className="bg-slate-50 rounded-xl p-6 max-w-md mx-auto text-left">
                <h3 className="font-semibold text-slate-900 mb-3">What happens next?</h3>
                <ul className="space-y-2 text-slate-600 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10c0a5] mt-0.5 flex-shrink-0" />
                    <span>You'll receive a confirmation email shortly</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10c0a5] mt-0.5 flex-shrink-0" />
                    <span>We'll notify you when your spot is ready</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10c0a5] mt-0.5 flex-shrink-0" />
                    <span>New clients launch in order of signup</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Footer text */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-8 text-slate-500 text-sm"
        >
          <p>
            Questions? Contact us at{' '}
            <a href="mailto:support@quoteplicity.com" className="text-[#10c0a5] hover:underline font-medium">
              support@quoteplicity.com
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default WaitlistFormWidgetV2;
